import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Handshake, TrendingUp, DollarSign, Award } from "lucide-react";

const applicationSchema = z.object({
  firstName: z.string().min(2, "First name is required"),
  lastName: z.string().min(2, "Last name is required"),
  email: z.string().email("Invalid email address"),
  phone: z.string().optional(),
  companyName: z.string().optional(),
  partnerType: z.enum(["consultant", "vendor", "coach", "former_admin", "other"]),
  agreeToTerms: z.boolean().refine((val) => val === true, "You must agree to the terms"),
});

type ApplicationFormData = z.infer<typeof applicationSchema>;

export default function ReferralPartnerApplication() {
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  const totalSteps = 3;

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<ApplicationFormData>({
    resolver: zodResolver(applicationSchema),
  });

  const partnerType = watch("partnerType");
  const agreeToTerms = watch("agreeToTerms");

  const generateReferralCode = (firstName: string, lastName: string): string => {
    const prefix = partnerType?.toUpperCase() || "PARTNER";
    const name = `${firstName}-${lastName}`.toUpperCase().replace(/\s+/g, '-');
    const timestamp = Date.now().toString().slice(-4);
    return `${prefix}-${name}-${timestamp}`;
  };

  const onSubmit = async (data: ApplicationFormData) => {
    setIsSubmitting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      const referralCode = generateReferralCode(data.firstName, data.lastName);
      
      const { error } = await supabase.from("referral_partners").insert({
        user_id: user?.id || null,
        partner_type: data.partnerType,
        first_name: data.firstName,
        last_name: data.lastName,
        email: data.email,
        phone: data.phone || null,
        company_name: data.companyName || null,
        referral_code: referralCode,
        status: "pending",
        onboarding_completed: true,
      });

      if (error) throw error;

      toast({
        title: "Application Submitted!",
        description: "We'll review your application and send you the referral kit within 24 hours.",
      });

      setTimeout(() => {
        navigate("/auth");
      }, 2000);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNext = () => {
    if (step < totalSteps) setStep(step + 1);
    else handleSubmit(onSubmit)();
  };

  const handleBack = () => {
    if (step > 1) setStep(step - 1);
  };

  return (
    <div className="min-h-screen bg-background py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold mb-4">Become a Referral Partner</h1>
          <p className="text-lg text-muted-foreground">
            Earn 15% commission on every institution you refer. Top performers earn $5K+ bonuses.
          </p>
        </div>

        {/* Benefits Grid */}
        <div className="grid md:grid-cols-4 gap-4 mb-8">
          <Card className="p-4 text-center">
            <DollarSign className="h-8 w-8 mx-auto mb-2 text-primary" />
            <h3 className="font-semibold mb-1">15% Commission</h3>
            <p className="text-sm text-muted-foreground">$4.5K avg per deal</p>
          </Card>
          <Card className="p-4 text-center">
            <TrendingUp className="h-8 w-8 mx-auto mb-2 text-primary" />
            <h3 className="font-semibold mb-1">$30K Avg Deal</h3>
            <p className="text-sm text-muted-foreground">Annual contracts</p>
          </Card>
          <Card className="p-4 text-center">
            <Award className="h-8 w-8 mx-auto mb-2 text-primary" />
            <h3 className="font-semibold mb-1">$5K Bonus</h3>
            <p className="text-sm text-muted-foreground">Top quarterly performer</p>
          </Card>
          <Card className="p-4 text-center">
            <Handshake className="h-8 w-8 mx-auto mb-2 text-primary" />
            <h3 className="font-semibold mb-1">Net-30 Terms</h3>
            <p className="text-sm text-muted-foreground">Fast payouts</p>
          </Card>
        </div>

        {/* Application Form */}
        <Card className="p-8">
          <div className="mb-6">
            <div className="flex justify-between mb-2">
              {[1, 2, 3].map((s) => (
                <div
                  key={s}
                  className={`h-2 flex-1 mx-1 rounded ${
                    s <= step ? "bg-primary" : "bg-muted"
                  }`}
                />
              ))}
            </div>
            <p className="text-sm text-muted-foreground text-center">
              Step {step} of {totalSteps}
            </p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)}>
            {step === 1 && (
              <div className="space-y-4">
                <h2 className="text-2xl font-semibold mb-4">Contact Information</h2>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="firstName">First Name *</Label>
                    <Input id="firstName" {...register("firstName")} />
                    {errors.firstName && (
                      <p className="text-sm text-destructive mt-1">{errors.firstName.message}</p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="lastName">Last Name *</Label>
                    <Input id="lastName" {...register("lastName")} />
                    {errors.lastName && (
                      <p className="text-sm text-destructive mt-1">{errors.lastName.message}</p>
                    )}
                  </div>
                </div>
                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input id="email" type="email" {...register("email")} />
                  {errors.email && (
                    <p className="text-sm text-destructive mt-1">{errors.email.message}</p>
                  )}
                </div>
                <div>
                  <Label htmlFor="phone">Phone</Label>
                  <Input id="phone" type="tel" {...register("phone")} />
                </div>
                <div>
                  <Label htmlFor="companyName">Company Name (if applicable)</Label>
                  <Input id="companyName" {...register("companyName")} />
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-4">
                <h2 className="text-2xl font-semibold mb-4">Background</h2>
                <div>
                  <Label htmlFor="partnerType">What best describes you? *</Label>
                  <Select
                    onValueChange={(value) =>
                      setValue("partnerType", value as any, { shouldValidate: true })
                    }
                    value={partnerType}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select partner type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="consultant">NIL/Compliance Consultant</SelectItem>
                      <SelectItem value="vendor">Athletic Department Vendor</SelectItem>
                      <SelectItem value="coach">Former Coach/Administrator</SelectItem>
                      <SelectItem value="former_admin">Former Athletic Director</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                  {errors.partnerType && (
                    <p className="text-sm text-destructive mt-1">{errors.partnerType.message}</p>
                  )}
                </div>
                <div className="bg-muted p-4 rounded-lg">
                  <h3 className="font-semibold mb-2">Ideal Referral Profile:</h3>
                  <ul className="space-y-1 text-sm">
                    <li>• Athletic Directors managing 15+ varsity sports</li>
                    <li>• Compliance Officers overseeing NIL programs</li>
                    <li>• Power 5, FCS, Div II/III institutions</li>
                    <li>• Schools concerned about Title IX and revenue sharing compliance</li>
                  </ul>
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-4">
                <h2 className="text-2xl font-semibold mb-4">Terms & Agreement</h2>
                <div className="bg-muted p-6 rounded-lg space-y-4 max-h-96 overflow-y-auto">
                  <h3 className="font-semibold">Referral Partner Agreement Summary</h3>
                  <div className="space-y-2 text-sm">
                    <p><strong>Commission:</strong> 15% of annual contract value</p>
                    <p><strong>Payment Terms:</strong> Net-30 after customer payment received</p>
                    <p><strong>Referral Requirements:</strong></p>
                    <ul className="list-disc ml-6">
                      <li>Provide warm introduction to decision maker</li>
                      <li>Referral code must be used at signup</li>
                      <li>Customer must complete annual contract</li>
                    </ul>
                    <p><strong>Accelerator Program:</strong></p>
                    <ul className="list-disc ml-6">
                      <li>Runs for 90 days from enrollment</li>
                      <li>Top performer: $5,000 bonus</li>
                      <li>2nd place: $2,000 bonus</li>
                      <li>3rd place: $1,000 bonus</li>
                    </ul>
                    <p><strong>Confidentiality:</strong> All customer information must remain confidential</p>
                    <p><strong>Termination:</strong> Either party may terminate with 30 days notice</p>
                  </div>
                </div>
                <div className="flex items-start space-x-2">
                  <Checkbox
                    id="agreeToTerms"
                    checked={agreeToTerms}
                    onCheckedChange={(checked) =>
                      setValue("agreeToTerms", checked as boolean, { shouldValidate: true })
                    }
                  />
                  <Label htmlFor="agreeToTerms" className="cursor-pointer">
                    I agree to the Referral Partner Agreement and understand the commission structure *
                  </Label>
                </div>
                {errors.agreeToTerms && (
                  <p className="text-sm text-destructive">{errors.agreeToTerms.message}</p>
                )}
              </div>
            )}

            <div className="flex justify-between mt-8">
              <Button type="button" variant="outline" onClick={handleBack} disabled={step === 1}>
                Back
              </Button>
              <Button
                type="button"
                onClick={handleNext}
                disabled={isSubmitting}
              >
                {step === totalSteps ? (isSubmitting ? "Submitting..." : "Submit Application") : "Next"}
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
}
